import React, { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import { Plus, Edit2, Trash2, X, Briefcase, Search } from "lucide-react";
import Swal from 'sweetalert2';
import { API_BASE_URL } from "../config";

export default function PoliticsNameManagement() {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [newName, setNewName] = useState("");
    const [politicsNames, setPoliticsNames] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");

    const fetchPoliticsNames = () => {
        setLoading(true);
        fetch(`${API_BASE_URL}/api/politics-names/all`)
            .then((res) => {
                if (!res.ok) throw new Error("Failed to fetch business names");
                return res.json();
            })
            .then((resJson) => {
                if (resJson && resJson.status && resJson.data) {
                    setPoliticsNames(resJson.data);
                } else {
                    throw new Error(resJson.message || "Invalid response format");
                }
                setLoading(false);
            })
            .catch((err) => {
                console.error("fetch error:", err);
                setError(err.message);
                setLoading(false);
            });
    };

    useEffect(() => {
        fetchPoliticsNames();
    }, []);

    const handleSave = () => {
        if (!newName.trim()) {
            Swal.fire("Error", "Please provide a name!", "error");
            return;
        }

        Swal.fire({
            title: "Saving...",
            allowOutsideClick: false,
            didOpen: () => Swal.showLoading()
        });

        const url = editingId
            ? `${API_BASE_URL}/api/politics-names/update/${editingId}`
            : `${API_BASE_URL}/api/politics-names/create`;
        const method = editingId ? "PUT" : "POST";

        fetch(url, {
            method,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name: newName })
        })
            .then(res => res.json())
            .then(resJson => {
                Swal.close();
                if (resJson && resJson.status) {
                    Swal.fire("Success", `Politics name ${editingId ? 'updated' : 'created'} successfully!`, "success");
                    fetchPoliticsNames();
                    resetForm();
                } else {
                    throw new Error(resJson.message || "Failed to save business name");
                }
            })
            .catch(err => {
                Swal.close();
                console.error("Save error:", err);
                Swal.fire("Error", err.message, "error");
            });
    };

    const resetForm = () => {
        setNewName("");
        setEditingId(null);
        setIsCreateModalOpen(false);
    };

    const handleEdit = (item) => {
        setNewName(item.name || "");
        setEditingId(item.id);
        setIsCreateModalOpen(true);
    };

    const handleDelete = (id) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        }).then((result) => {
            if (result.isConfirmed) {
                Swal.fire({ title: "Deleting...", allowOutsideClick: false, didOpen: () => Swal.showLoading() });
                fetch(`${API_BASE_URL}/api/politics-names/delete/${id}`, { method: "DELETE" })
                    .then(res => res.json())
                    .then(resJson => {
                        Swal.close();
                        if (resJson && resJson.status) {
                            Swal.fire('Deleted!', 'Politics name has been deleted.', 'success');
                            fetchPoliticsNames();
                        } else {
                            throw new Error(resJson.message || "Failed to delete");
                        }
                    })
                    .catch(err => {
                        Swal.close();
                        console.error("Delete error:", err);
                        Swal.fire("Error", err.message, "error");
                    });
            }
        });
    };

    return (
        <div className="bg-[#f8fafc] dark:bg-slate-900 min-h-screen text-slate-800 dark:text-slate-100 font-sans relative overflow-hidden">
            <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

            <div className="flex-1 lg:ml-64 flex flex-col min-h-screen relative z-10 w-full lg:w-[calc(100%-16rem)]">
                <Header
                    onMenuClick={() => setIsSidebarOpen(true)}
                    title="Politics Categories"
                    subtitle="Manage Politics Categories (Names)."
                />

                <div className="p-4 sm:p-8 flex-1 flex flex-col w-full overflow-hidden">
                    <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row lg:items-center justify-between gap-4">
                        <div className="relative w-full sm:w-72">
                            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                            <input
                                type="text"
                                placeholder="Search politics categories..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full bg-white dark:bg-slate-800/80 backdrop-blur-md border border-white dark:border-slate-700 shadow-[0_4px_20px_rgba(0,0,0,0.03)] rounded-xl pl-9 pr-4 py-2.5 text-sm outline-none focus:border-blue-500 transition-all dark:text-slate-100"
                            />
                        </div>

                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => { resetForm(); setIsCreateModalOpen(true); }}
                                className="flex-1 lg:flex-none justify-center bg-blue-600 hover:bg-blue-700 text-white shadow-sm hover:shadow-md px-4 py-2 rounded-xl text-sm font-medium transition-all flex items-center gap-2">
                                <Plus size={16} />
                                Create Politics Category
                            </button>
                        </div>
                    </div>

                    {error && <div className="mb-6 p-4 bg-rose-50 border border-rose-100 text-rose-600 rounded-2xl">{error}</div>}

                    {loading ? (
                        <div className="flex items-center justify-center py-12 flex-1 animate-pulse">Loading...</div>
                    ) : politicsNames.length === 0 ? (
                        <div className="flex items-center justify-center py-12 flex-1">No politics categories found.</div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {politicsNames.filter(item => item.name.toLowerCase().includes(searchTerm.toLowerCase())).map(item => (
                                <div key={item.id} className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border p-6 flex flex-col">
                                    <h3 className="text-lg font-bold mb-4">{item.name}</h3>

                                    <div className="mt-auto flex gap-2 pt-4 border-t">
                                        <button onClick={() => handleEdit(item)} className="flex-1 flex gap-1 justify-center py-2 text-sm text-slate-600 hover:text-blue-600 bg-slate-50 rounded-lg">
                                            <Edit2 size={16} /> Edit
                                        </button>
                                        <button onClick={() => handleDelete(item.id)} className="flex-1 flex gap-1 justify-center py-2 text-sm text-rose-600 hover:text-rose-700 bg-rose-50 rounded-lg">
                                            <Trash2 size={16} /> Delete
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {isCreateModalOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={() => setIsCreateModalOpen(false)}></div>
                    <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 w-full max-w-md relative z-10 shadow-xl overflow-y-auto max-h-[90vh]">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-lg font-bold">{editingId ? 'Edit Category' : 'Create Category'}</h3>
                            <button onClick={() => setIsCreateModalOpen(false)}><X size={20} /></button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1.5">Category Name</label>
                                <input
                                    type="text"
                                    value={newName}
                                    onChange={e => setNewName(e.target.value)}
                                    className="w-full bg-slate-50 border rounded-xl px-4 py-2 text-sm outline-none focus:border-blue-500"
                                />
                            </div>
                        </div>

                        <div className="mt-8 flex justify-end gap-3">
                            <button onClick={() => setIsCreateModalOpen(false)} className="px-4 py-2 bg-slate-100 rounded-xl">Cancel</button>
                            <button onClick={handleSave} className="px-4 py-2 bg-blue-600 text-white rounded-xl">Save</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
